import React, { useEffect, useState } from 'react';
import { graphqlQuery } from '../utils/graphqlApi';
import { getEvalSessionDetail } from '../graphql/queries';

// Shown in any cell that has no data.
const MISSING = '--';

const costText = (total, currency) => {
  if (total == null || !Number.isFinite(Number(total))) return null;
  const amount = `$${Number(total).toFixed(4)}`;
  return currency && currency !== 'USD' ? `${amount} ${currency}` : amount;
};

// AWSJSON arrives as a string. Return the text to display for a rationale /
// request / response value, or null when there is nothing to show.
//   - JSON object / array -> pretty-printed JSON
//   - JSON string         -> the string itself
//   - anything else       -> the raw text
const cellText = (value) => {
  if (value == null) return null;
  let parsed = value;
  try {
    parsed = JSON.parse(value);
  } catch {
    // not JSON: plain text
  }
  let text;
  if (parsed !== null && typeof parsed === 'object') text = JSON.stringify(parsed, null, 2);
  else if (typeof parsed === 'string') text = parsed;
  else text = String(value);
  text = text.trim();
  return text === '' ? null : text;
};

// One table row per evals row (DB entry) of the session. Session-level values
// (the cost) repeat on every row. A session without records (for example one
// that only has a cost row) falls back to the single-row fields.
const buildRows = (session, detail) => {
  if (!detail) {
    // Detail not loaded yet (or unavailable): show what the report row knows.
    return [
      {
        key: 'report-row',
        sentiment: session.sentiment,
        feedbackText: session.feedback_text,
        routedTo: null,
        cost: null,
        rationale: null,
        request: null,
        response: null,
        detailPending: true,
      },
    ];
  }

  const cost = costText(detail.total_cost, detail.currency);
  const records = Array.isArray(detail.records) ? detail.records : [];

  if (records.length === 0) {
    return [
      {
        key: 'single-row',
        sentiment: session.sentiment,
        feedbackText: session.feedback_text,
        routedTo: detail.routed_to,
        cost,
        rationale: cellText(detail.rationale),
        request: cellText(detail.request_context),
        response: cellText(detail.response_context),
      },
    ];
  }

  return records.map((r, i) => ({
    key: `${r.eval_id}|${r.created_at}|${i}`,
    sentiment: r.sentiment_of_feedback,
    feedbackText: r.written_feedback,
    routedTo: r.routed_skill,
    cost,
    rationale: cellText(r.rationale),
    request: cellText(r.request_context),
    response: cellText(r.response_context),
  }));
};

// Session-scoped cache so re-opening the same session is instant.
const cache = new Map();

export default function EvalSessionDetailModal({ session, evalId, agentId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!session) return undefined;
    const key = `${agentId}|${evalId}|${session.session_id}`;
    if (cache.has(key)) {
      setDetail(cache.get(key));
      setError(null);
      setIsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setDetail(null);
    graphqlQuery(getEvalSessionDetail, {
      input: { session_id: session.session_id, eval_id: evalId, agent_id: agentId },
    })
      .then((data) => {
        if (cancelled) return;
        const d = data?.getEvalSessionDetail ?? null;
        cache.set(key, d);
        setDetail(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session, evalId, agentId]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!session) return null;

  const rows = buildRows(session, detail);

  // A value, or `…` while the detail is still loading, or `--` when there is none.
  const show = (row, value) => {
    if (value != null && value !== '') return value;
    return row.detailPending && isLoading ? '…' : MISSING;
  };

  return (
    <div className="ge-modal-backdrop" onClick={onClose}>
      <div
        className="ge-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ge-session-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ge-modal-header">
          <h3 id="ge-session-title">Session details</h3>
          <button type="button" className="ge-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {isLoading && <div className="ge-muted" style={{ marginTop: 12 }}>Loading session…</div>}
        {error && <div className="ge-bad" style={{ marginTop: 12 }}>{error}</div>}
        {!isLoading && !error && detail === null && (
          <div className="ge-muted" style={{ marginTop: 12 }}>No stored detail for this session.</div>
        )}

        <div className="ge-modal-scroll">
          <table className="ge-modal-table ge-session-table">
            <thead>
              <tr>
                <th>Session id</th>
                <th>Feedback</th>
                <th>Routed to</th>
                <th>Cost</th>
                <th>Rationale</th>
                <th>Request context</th>
                <th>Response context</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td className="ge-skill">{session.session_id}</td>
                  <td>
                    <div>{show(row, row.sentiment)}</div>
                    {row.feedbackText && <div className="ge-cell-sub ge-cell-text">{row.feedbackText}</div>}
                  </td>
                  <td className="ge-skill">{show(row, row.routedTo)}</td>
                  <td>{show(row, row.cost)}</td>
                  <td>
                    <div className="ge-cell-text ge-cell-scroll">{show(row, row.rationale)}</div>
                  </td>
                  <td>
                    <div className="ge-cell-text ge-cell-scroll">{show(row, row.request)}</div>
                  </td>
                  <td>
                    <div className="ge-cell-text ge-cell-scroll">{show(row, row.response)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
